import React from 'react';
/**
 * Selector column width: 1.6rem pad + 1.6rem checkbox + 1.2rem pad.
 * Matches TableStyles.scss `table-column--selector` width.
 */
export const SELECTOR_COL_WIDTH = '4.4rem';
/**
 * Get cell content using column definition.
 * Coerces non-scalars to a string to avoid React error #31.
 */
export function getCellTemplate({ column, row, rowIndex }) {
    var _a, _b, _c;
    if (column.renderer) {
        return React.createElement(column.renderer, {
            item: row,
            dataKey: column.dataKey,
            index: rowIndex,
            key: `table-cell-${column.dataKey}-${rowIndex}`
        }, null);
    }
    const value = row[column.dataKey];
    if (value === null || value === undefined)
        return '';
    if (typeof value === 'object') {
        const obj = value;
        const label = (_c = (_b = (_a = obj.name) !== null && _a !== void 0 ? _a : obj.display_name) !== null && _b !== void 0 ? _b : obj.label) !== null && _c !== void 0 ? _c : obj.title;
        if (typeof label === 'string' || typeof label === 'number')
            return String(label);
        try {
            return JSON.stringify(value);
        }
        catch (_d) {
            return '[complex object]';
        }
    }
    return value;
}
/**
 * Map a ColumnType to the CSS class applied to th/td elements.
 */
export function getColumnClass(columnType, isLast) {
    if (columnType === 'primary')
        return 'table-column--primary';
    if (columnType === 'action')
        return 'table-column--action';
    if (columnType === 'last' || isLast)
        return 'table-column--last';
    return '';
}
/**
 * Sanitize an ID value to be safe for use in HTML id attributes.
 * Replaces non-alphanumeric characters with hyphens.
 */
export function sanitizeId(id) {
    const stringId = String(id);
    return stringId.replace(/[^a-zA-Z0-9_-]/g, '-').replace(/-+/g, '-');
}
/**
 * Calculate sticky `right` offset for each action column, starting from the last.
 * Columns with an explicit `width` contribute to the accumulated offset.
 */
export function buildActionOffsets(columns) {
    const actionCols = columns.filter((c) => c.columnType === 'action');
    const offsets = new Map();
    let accumulated = '0px';
    for (let i = actionCols.length - 1; i >= 0; i -= 1) {
        const col = actionCols[i];
        offsets.set(col.dataKey, accumulated);
        if (col.width) {
            accumulated = accumulated === '0px' ? col.width : `calc(${accumulated} + ${col.width})`;
        }
    }
    return offsets;
}
/**
 * Validates which columns may pin, and in what order.
 *
 * It deliberately does NOT compute offsets. Those are measured from the rendered header cells by
 * `usePinnedColumnOffsets`, because a column's declared `width` is not the width it renders at —
 * padding and box-sizing push it out, and the selection column declares no width at all. Deriving
 * offsets from the declared values made each frozen column stick slightly left of where it sits,
 * so the block slid sideways before locking.
 *
 * Pinned columns must be contiguous from the start of the table: `position: sticky` offsets are
 * absolute, so a pinned column with an unpinned one before it would overlap on scroll rather than
 * sit beside its neighbours. Non-contiguous pins are dropped with a dev warning instead of
 * rendering something subtly broken.
 */
export function buildPinnedColumnLayout(columns) {
    let pinnedCount = 0;
    while (pinnedCount < columns.length && columns[pinnedCount].pinned)
        pinnedCount += 1;
    if (process.env.NODE_ENV !== 'production') {
        const strays = columns.slice(pinnedCount).filter((c) => c.pinned);
        if (strays.length > 0) {
            // eslint-disable-next-line no-console
            console.warn(`[Table] Ignoring pinned column(s) ${strays.map((c) => c.dataKey).join(', ')}: pinned ` +
                'columns must be contiguous from the leftmost column. Pin every column before them, or ' +
                'drop the flag.');
        }
    }
    if (pinnedCount === 0)
        return { pinnedKeys: [], hasPinnedColumns: false };
    const pinnedKeys = columns.slice(0, pinnedCount).map((c) => c.dataKey);
    return { pinnedKeys, hasPinnedColumns: true, lastPinnedKey: pinnedKeys[pinnedKeys.length - 1] };
}
/**
 * Toolbar overflow measures each Group child as one item. Flatten fragments
 * and arrays so individual mass actions can hide independently.
 */
export function toolbarGroupItems(content, selection) {
    const rendered = typeof content === 'function' ? content(selection) : content;
    if (React.isValidElement(rendered) && rendered.type === React.Fragment) {
        return React.Children.toArray(rendered.props.children);
    }
    return React.Children.toArray(rendered);
}
